import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { requestResetPassword } from "../../actions/auth";
import TokenService from "../../services/token";
import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import rutas from "../../routes/constants/routes";

interface Props {
  docType: { id: number; name: string }[];
  requestResetPassword: (data: FormData) => void;
  successPasswordReset: boolean;
  loading: boolean;
  errorPasswordReset: string;
  error: any;
}

interface FormData {
  identification: string;
  email: string;
}

const ResetPassword = ({
  docType,
  requestResetPassword,
  successPasswordReset,
  loading,
  errorPasswordReset,
}: Props) => {
  const [formData, setFormData] = useState<FormData>({
    identification: "",
    email: "",
  });
  const history = useHistory();

  const [errors, setErrors] = useState<Partial<FormData>>({});

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "identification") {
      const formattedValue = formatCuitCuil(value);

      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (successPasswordReset) {
      localStorage.removeItem("LocalSession");
      localStorage.removeItem("selectedClient");
      TokenService.clear();
      setTimeout(() => history.push(`${rutas.LOGIN}`), 2000);
    }
  }, [successPasswordReset]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Partial<FormData> = {};
    if (!formData.email) {
      newErrors.email = "El email es obligatorio";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Ingrese un email válido";
    }

    if (!formData.identification) {
      newErrors.identification = "Ingrese un CUIT/CUIL";
    } else if (!isValidCuitCuil(formData.identification)) {
      newErrors.identification = "Formato inválido. Debe ser xx-xxxxxxxx-x";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      requestResetPassword({
        ...formData,
        identification: formData.identification.replace(/-/g, ""),
      });
    }
  };

  const isValidCuitCuil = (value: string): boolean => {
    return /^\d{2}-\d{8}-\d{1}$/.test(value);
  };

  const formatCuitCuil = (value: string): string => {
    const numbers = value.replace(/\D/g, "");

    if (numbers.length <= 2) {
      return numbers;
    }

    if (numbers.length <= 10) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    }

    return `${numbers.slice(0, 2)}-${numbers.slice(2, 10)}-${numbers.slice(
      10,
      11,
    )}`;
  };

  return (
    <Box display="flex" justifyContent="center" py={6}>
      <Box width={{ xs: "100%", md: "500px" }} px={2}>
        <Box display="flex" justifyContent="center" mb={3}>
          <Box
            width={45}
            height={45}
            bgcolor="#E3000B"
            borderRadius="50%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <LockIcon sx={{ color: "#fff" }} />
          </Box>
        </Box>
        <Typography variant="h5" align="center" mb={4}>
          Recuperar Contraseña
        </Typography>

        <form onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Número de documento"
            name="identification"
            value={formData.identification}
            onChange={handleChange}
            error={!!errors.identification}
            helperText={errors.identification}
            margin="normal"
            inputProps={{
              maxLength: 13,
            }}
          />

          <Box mt={3}>
            <Button
              variant="contained"
              color="error"
              type="submit"
              fullWidth
              disabled={loading}
              sx={{ py: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Recuperar contraseña"
              )}
            </Button>
          </Box>

          {successPasswordReset && (
            <Alert severity="success" sx={{ mt: 3 }}>
              {successPasswordReset}
            </Alert>
          )}
          {errorPasswordReset && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {errorPasswordReset
                ? errorPasswordReset
                : "Error al envíar el email. Por favor, intente nuevamente."}
            </Alert>
          )}
        </form>
      </Box>
    </Box>
  );
};

export default connect(
  (state: any) => ({
    docType: state.params.docType,
    successPasswordReset: state.auth.successPasswordReset,
    loading: state.auth.loading,
    errorPasswordReset: state.auth.errorPasswordReset,
    error: state.auth.error,
  }),
  (dispatch: any) => ({
    requestResetPassword: (data: FormData) =>
      dispatch(requestResetPassword(data)),
  }),
)(ResetPassword);
